import { UserManager } from 'oidc-client';
import { LOCAL_STORAGE } from 'src/app/core/constant/local-storage.constants';

export class LogoutSession {
    active = false;
    private pending: Promise<void> | null = null;
    private readonly storageKey = 'da.oidc.logout';
    private readonly channelKey = 'da.oidc.front-channel';
    private readonly onSignedOut = () => {
        void this.clear().then(this.showSignedOut, this.showSignedOut);
    };
    private readonly onStorage = (event: StorageEvent) => {
        if (event.key === this.storageKey && event.newValue) {
            void this.clear(false).then(this.showSignedOut, this.showSignedOut);
        } else if (event.key === this.channelKey && event.newValue) {
            try {
                const notification = JSON.parse(event.newValue);
                if (typeof notification.iss !== 'string' || typeof notification.sid !== 'string') return;
                void this.frontChannel(new URLSearchParams({ iss: notification.iss, sid: notification.sid }), false)
                    .then(this.showSignedOut, () => undefined);
            } catch {
                return;
            }
        }
    };
    private readonly onUserLoaded = () => {
        if (this.active) {
            this.clearMemory();
            void this.manager.removeUser().catch(() => undefined);
        }
    };

    constructor(private manager: UserManager, private clearMemory: () => void,
        private showSignedOut: () => void) {
        this.manager.events.addUserSignedOut(this.onSignedOut);
        this.manager.events.addUserLoaded(this.onUserLoaded);
        window.addEventListener('storage', this.onStorage);
    }

    resume(): void {
        this.active = false;
        this.pending = null;
    }

    start(): Promise<void> {
        if (!this.pending) {
            this.active = true;
            this.manager.stopSilentRenew();
            this.clearMemory();
            this.pending = this.redirect();
        }
        return this.pending;
    }

    private async redirect(): Promise<void> {
        let current;
        try {
            current = await this.manager.getUser();
        } finally {
            await this.clear();
        }
        await this.manager.signoutRedirect({ id_token_hint: current?.id_token });
    }

    async complete(): Promise<void> {
        this.active = true;
        this.manager.stopSilentRenew();
        this.clearMemory();
        try {
            await this.manager.signoutRedirectCallback();
        } finally {
            await this.clear();
        }
    }

    async frontChannel(parameters = new URLSearchParams(window.location.search), relay = true): Promise<void> {
        if (parameters.get('iss')?.replace(/\/$/, '') !== this.manager.settings.authority.replace(/\/$/, '') ||
            !parameters.get('sid')) {
            throw new Error('Invalid logout notification');
        }
        const current = await this.manager.getUser();
        if (!current && relay) {
            localStorage.setItem(this.channelKey, JSON.stringify({
                iss: parameters.get('iss'), sid: parameters.get('sid'), signal: Date.now() + ':' + Math.random()
            }));
            localStorage.removeItem(this.channelKey);
            return;
        }
        if (parameters.get('sid') !== current?.profile?.sid) throw new Error('Invalid logout notification');
        await this.clear();
    }

    async clear(notify = true): Promise<void> {
        this.active = true;
        this.manager.stopSilentRenew();
        this.clearMemory();
        for (const key of [LOCAL_STORAGE.TOKEN, LOCAL_STORAGE.USER, 'da-menu', 'All-Menu',
            'gridRequest', 'filterValue', 'appliedFilters', 'clinics', 'validate', 'pageUrl',
            'vendorClientSelection', 'usedNumberStartBot', 'usedNumberStartCall']) {
            localStorage.removeItem(key);
        }
        try {
            await this.manager.removeUser();
        } finally {
            if (notify) localStorage.setItem(this.storageKey, Date.now() + ':' + Math.random());
        }
    }

    dispose(): void {
        this.manager.events.removeUserSignedOut(this.onSignedOut);
        this.manager.events.removeUserLoaded(this.onUserLoaded);
        window.removeEventListener('storage', this.onStorage);
    }
}